import { Box, Typography } from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import React, { useState } from 'react';

import InfoRowAction from './InfoRowAction';
import InfoRowDetailDialog from './InfoRowDetailDialog';
import { DocumentConciliationRateHistory } from './types';

interface InfoRowProps {
  label: string;
  value: string | null | undefined;
  icon: React.ReactNode;
  maxLength?: number;
  actionIcon?: React.ReactNode;
  actionTooltipText?: string;
  documentId?: string;
  rateHistory?: DocumentConciliationRateHistory | null;
  onHistoryLoaded?: (history: DocumentConciliationRateHistory) => void;
}

const InfoRow: React.FC<InfoRowProps> = ({
  label,
  value,
  icon,
  maxLength = 40,
  actionIcon,
  actionTooltipText,
  documentId,
  rateHistory,
  onHistoryLoaded,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const hasValue = value !== null && value !== undefined && value !== '';

  const displayValue = hasValue ? value : 'No hay información';
  const textToShow =
    hasValue && maxLength
      ? displayValue.length > maxLength
        ? displayValue.slice(0, maxLength) + '...'
        : displayValue
      : displayValue;

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            backgroundColor: hasValue
              ? 'rgba(25, 118, 210, 0.1)'
              : 'rgba(158, 158, 158, 0.1)',
            borderRadius: '50%',
            p: 0.4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '24px',
            height: '24px',
          }}
        >
          {React.cloneElement(icon as React.ReactElement, {
            sx: {
              ...(icon as React.ReactElement).props.sx,
              color: hasValue
                ? (icon as React.ReactElement).props.sx?.color || 'primary.main'
                : 'text.disabled',
            },
          })}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.8rem', display: 'block', mb: 0.2 }}
          >
            {label}
          </Typography>
          {hasValue ? (
            <LightTooltip title={<Typography padding={1}>{value}</Typography>}>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ fontSize: '0.9rem', lineHeight: 1.2 }}
              >
                {textToShow}
              </Typography>
            </LightTooltip>
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.9rem',
                lineHeight: 1.2,
                color: 'text.disabled',
                fontStyle: 'italic',
              }}
            >
              {displayValue}
            </Typography>
          )}
        </Box>
        {actionIcon && actionTooltipText && (
          <InfoRowAction
            icon={actionIcon}
            onClick={handleOpenDialog}
            tooltipText={actionTooltipText}
          />
        )}
      </Box>
      {actionIcon && (
        <InfoRowDetailDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          label={label}
          value={value || null}
          documentId={documentId}
          rateHistory={rateHistory}
          onHistoryLoaded={onHistoryLoaded}
        />
      )}
    </>
  );
};

export default InfoRow;
